/**
 * A Vault's Configuration tab: the settings form the Vault describes itself (grouped, typed, with where each value
 * comes from), a sticky save bar, what happened to the last save (applied, restart required, rejected, rolled back),
 * the restart with its progress, and the list of changes. Cloud overrides are the only thing written here; vault.json
 * on the server stays the bootstrap. Pure rules (diff, coercion, source, restart progress) live in lib/vaultconfig.ts.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useBlocker } from 'react-router';
import { clearDraft, readDraft, writeDraft } from '../lib/vaultdraft.ts';
import { Api, ApiError, type ConfigSetting, type SettingValue, type VaultConfigData, type VaultDetailData } from '../lib/api.ts';
import { useAsync } from '../lib/hooks.ts';
import { ago, dateTime } from '../lib/format.ts';
import { acmeStateView, directoryInfo, relativeWords, type AcmeInfo } from '../lib/certificate.ts';
import { authorName } from '../lib/vault.ts';
import {
  FAST_WINDOW_MS, JOURNAL_COMMAND, RESTART_COMMAND, SOURCE_LABEL, STATUS_COMMAND, answered, beginRestart, changeCount, configStatus, currentView, labelOf,
  pendingChanges, pollInterval, rebase, restartElapsed, settingGroups, sourceOf, stepRestart, switchTlsSource, tlsSourceOf,
  TLS_GROUP, TLS_IMPLIED_KEYS, TLS_SOURCES, TLS_SOURCE_HELP, TLS_SOURCE_KEYS, TLS_SOURCE_LABEL, type Edit, type Edits, type RestartState, type TlsSource, type Values,
} from '../lib/vaultconfig.ts';
import { Kicker, Pill, Surface, cx } from '../design/primitives.tsx';
import { AlertIcon, CheckIcon, ChevronIcon, CopyIcon, RestartIcon, RevertIcon } from '../components/icons.tsx';
import { Button, Spinner, Switch, inputClass, useToast } from '../components/ui.tsx';
import { Confirm } from './Settings.tsx';

const message = (e: unknown) => (e instanceof ApiError ? e.message : String(e));
const CONFIG_POLL_MS = 15_000;

// ---------------------------------------------------------------- restart (state lives above the tabs)

export interface RestartCtl { state: RestartState; begin: () => Promise<void>; dismiss: () => void }

/** Restart a Vault and follow it: waits until it is online again AND reports a later start; gives up (kindly) after 120 s. */
export function useRestart(tid: string, v: VaultDetailData | undefined, reload: () => void): RestartCtl {
  const [state, setState] = useState<RestartState>({ kind: 'idle' });
  const [now, setNow] = useState(() => Date.now());
  const waiting = state.kind === 'waiting';
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [waiting]);
  useEffect(() => {
    if (!v) return;
    setState((s) => stepRestart(s, { now: Date.now(), online: v.online, lastStartAt: v.config?.lastStart?.at ?? null, version: v.version }));
  }, [v, now]);
  return {
    state,
    begin: async () => {
      await Api.restartVault(tid, v!.id);
      setState(beginRestart(Date.now(), v!.config?.lastStart?.at ?? null));
      reload();
    },
    dismiss: () => setState({ kind: 'idle' }),
  };
}

// ---------------------------------------------------------------- small parts

function Command({ text }: { text: string }) {
  const toast = useToast();
  return (
    <button type="button" title="Copy" onClick={() => { void navigator.clipboard?.writeText(text); toast('Copied'); }}
      className="inline-flex items-center gap-2 max-w-full rounded-chip bg-n3 hover:bg-n4 border-0 px-2.5 py-1.5 font-mono text-micro text-n10 cursor-pointer text-left focus-visible:ring-[1.5px] focus-visible:ring-accent group">
      <span className="[overflow-wrap:anywhere]">{text}</span>
      <CopyIcon size={13} className="shrink-0 text-n7 group-hover:text-n10" />
    </button>
  );
}

type Dot = 'ok' | 'warn' | 'fail' | 'idle';
const DOT: Record<Dot, string> = { ok: 'bg-ok', warn: 'bg-warn', fail: 'bg-fail', idle: 'bg-n6' };

/** A notice: a dot or icon for the state, a headline, quiet detail. Never a coloured fill. */
function Notice({ tone, title, children, action, role = 'status' }: { tone: Dot; title: string; children?: ReactNode; action?: ReactNode; role?: 'status' | 'alert' }) {
  return (
    <div role={role} className="flex items-start gap-3 flex-wrap">
      <i className={cx('size-2 rounded-full shrink-0 mt-[7px]', DOT[tone])} aria-hidden="true" />
      <div className="flex-1 min-w-[220px] flex flex-col gap-1">
        <span className="text-body font-medium">{title}</span>
        {children && <div className="text-small text-n8 flex flex-col gap-2 [&_p]:m-0">{children}</div>}
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------- the tab

export function ConfigTab({ v, tid, restart }: { v: VaultDetailData; tid: string; restart: RestartCtl }) {
  const toast = useToast();
  const [fastUntil, setFastUntil] = useState(0);
  const [, force] = useState(0);
  const fast = restart.state.kind === 'waiting' ? 2000 : pollInterval(fastUntil, Date.now(), CONFIG_POLL_MS);
  const cfg = useAsync(() => Api.vaultConfig(tid, v.id), [tid, v.id], fast);
  const data = cfg.data;
  // The draft survives leaving the page and Back (see lib/vaultdraft.ts)
  const [edits, setEdits] = useState<Edits>(() => readDraft(v.id).edits);
  const [picked, setPicked] = useState<TlsSource | null>(() => readDraft(v.id).picked);
  const resetDraft = () => { setEdits({}); setPicked(null); clearDraft(v.id); };
  const [submitted, setSubmitted] = useState<number>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [advanced, setAdvanced] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [restartError, setRestartError] = useState<string>();

  useEffect(() => {
    if (fastUntil <= Date.now()) return;
    const t = setTimeout(() => force((x) => x + 1), fastUntil - Date.now() + 50);
    return () => clearTimeout(t);
  }, [fastUntil]);

  // Once the restart is done the report changes with it: look at once
  const restartKind = restart.state.kind;
  useEffect(() => { if (restartKind === 'back') cfg.reload(); }, [restartKind]); // eslint-disable-line react-hooks/exhaustive-deps

  const view = data ? currentView(data) : null;
  const schema = data?.report?.schema ?? [];
  const pending = useMemo(
    () => (view ? pendingChanges(schema, view.effective, view.overrides, edits) : { changes: {}, errors: {}, count: 0 }),
    [schema, view, edits], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const secretKeys = useMemo(() => new Set(schema.filter((x) => x.type === 'secret' || x.sensitive).map((x) => x.key)), [schema]);
  useEffect(() => { if (schema.length > 0) writeDraft(v.id, edits, picked, secretKeys); }, [v.id, edits, picked, schema, secretKeys]);
  // A typed secret is the one thing a draft cannot keep: leaving asks first
  const hasSecretEdit = Object.keys(edits).some((k) => secretKeys.has(k));
  const blocker = useBlocker(hasSecretEdit && !saving);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('You have typed a secret that has not been saved. It is not kept when you leave. Leave anyway?')) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  // What happened to the save: the Vault answered with a newer version (applied, restart needed) or refused it
  useEffect(() => {
    if (submitted === undefined || !data?.report) return;
    const r = data.report;
    if (r.version >= submitted) {
      setSubmitted(undefined);
      resetDraft();
      toast(r.state === 'pending-restart' ? 'Saved. Restart required to take effect' : 'Applied');
    } else if (answered(data)) {
      setSubmitted(undefined); // the edits stay, so the values can be corrected
      toast(r.state === 'rolled-back' ? 'The Vault went back to its last working configuration' : 'The Vault rejected the settings; nothing was applied', 'fail');
    } else if (Date.now() > fastUntil) {
      setSubmitted(undefined);
      resetDraft();
    }
  }, [data, submitted, fastUntil]); // eslint-disable-line react-hooks/exhaustive-deps

  if (cfg.error && !data) {
    return <Surface className="p-8 text-center text-small text-n8">{cfg.error}</Surface>;
  }
  if (!data) return <Spinner />;
  const report = data.report;
  const status = configStatus(data);

  if (!report || !view) {
    return (
      <>
        <OfflineNotice online={data.online} />
        <Surface className="p-6 flex flex-col gap-3">
          <Notice tone="idle" title="This Vault has not reported its settings yet">
            <p>This Vault has not reported its settings yet. Update it to the latest version.</p>
          </Notice>
        </Surface>
        <History data={data} schema={[]} />
      </>
    );
  }

  const errorsByKey = new Map<string, string>();
  if (report.state === 'rejected' || report.state === 'rolled-back') for (const e of report.errors ?? []) if (e.key) errorsByKey.set(e.key, e.message);
  const setEdit = (key: string, edit: Edit | undefined) => setEdits((cur) => {
    const next = { ...cur };
    if (edit === undefined) delete next[key]; else next[key] = edit;
    return next;
  });

  const save = async () => {
    setSaving(true);
    setSaveError(undefined);
    try {
      const r = await Api.saveVaultConfigData(tid, v.id, rebase(schema, data, pending.changes));
      setFastUntil(Date.now() + FAST_WINDOW_MS);
      if (r.pushed) setSubmitted(r.version);
      else { resetDraft(); toast('Saved. Delivered when the Vault reconnects'); }
      cfg.reload();
    } catch (e) {
      setSaveError(message(e));
    } finally {
      setSaving(false);
    }
  };

  const groups = settingGroups(schema);
  const labels = (keys: string[]) => keys.map((k) => labelOf(schema, k)).join(', ');
  const restartKeys = report.restartRequired ?? [];
  const canSave = pending.count > 0 && Object.keys(pending.errors).length === 0 && !saving && submitted === undefined;
  const invalid = Object.keys(pending.errors).length;
  const overridesJson = JSON.stringify(data.desired?.overrides ?? report.overrides ?? {}, null, 2);

  return (
    <>
      <OfflineNotice online={data.online} />

      <Surface className="p-5 flex flex-col gap-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <Kicker>Status</Kicker>
          <span className="text-micro text-n7">
            version {report.version}{data.reportedAt ? ` · reported ${ago(data.reportedAt)}` : ''}
            {report.lastStart?.at ? ` · started ${ago(report.lastStart.at)}` : ''}
          </span>
        </div>
        {status.kind === 'applied' && <Notice tone="ok" title="Applied">The Vault runs these settings.</Notice>}
        {status.kind === 'delivering' && (
          <div className="flex items-center gap-3 text-body font-medium" role="status"><Spinner className="size-4" />Waiting for the Vault to apply version {data.desired?.version}…</div>
        )}
        {status.kind === 'queued' && <Notice tone="warn" title={`Version ${data.desired?.version} is saved`}>It is delivered when the Vault reconnects.</Notice>}
        {status.kind === 'pending-restart' && (
          <Notice tone="warn" title={`Restart required for: ${labels(restartKeys)}`}
            action={<RestartButton disabled={!data.online || !report.canRestart || restart.state.kind === 'waiting'} onClick={() => setConfirming(true)} />}>
            <p>The settings are saved on the Vault. The other changes already apply; these take effect at the next start.</p>
          </Notice>
        )}
        {status.kind === 'rejected' && (
          <Notice tone="fail" role="alert" title="The Vault did not apply the settings. Nothing was changed.">
            <ul className="m-0 pl-4 flex flex-col gap-1">
              {(report.errors ?? []).map((e, i) => <li key={i}>{e.key ? <><span className="text-n10">{labelOf(schema, e.key)}</span>: </> : null}{e.message}</li>)}
            </ul>
            <p>Correct the values below and save again.</p>
          </Notice>
        )}
        {status.kind === 'rolled-back' && (
          <Notice tone="fail" role="alert" title="The Vault went back to its last working configuration">
            <p>
              {report.lastStart?.rolledBack
                ? `Version ${report.lastStart.rolledBack.from} did not start cleanly, so the Vault returned to version ${report.lastStart.rolledBack.to}. Reason: ${report.lastStart.rolledBack.reason}`
                : (report.errors ?? []).map((e) => e.message).join(' ') || 'The previous settings did not start cleanly.'}
            </p>
            <p>The same version is not sent again. Your next edit creates a new version automatically.</p>
          </Notice>
        )}
      </Surface>

      <RestartCard canRestart={report.canRestart} online={data.online} restart={restart} confirming={confirming} setConfirming={setConfirming} error={restartError} setError={setRestartError} />

      <div className="flex flex-col gap-3">
        {groups.map((g) => {
          const open = !closed.has(g.name) || g.settings.some((s) => errorsByKey.has(s.key) || s.key in pending.changes || s.key in pending.errors);
          const toggle = () => setClosed((cur) => { const n = new Set(cur); if (n.has(g.name)) n.delete(g.name); else n.add(g.name); return n; });
          // HTTPS ingest: the certificate source is chosen first, only its settings are rows
          const tlsGroup = g.name === TLS_GROUP && g.settings.some((s) => s.key === 'tls.enabled' || s.key === 'tls.acme.enabled');
          const derived = tlsSourceOf(view.effective);
          const source: TlsSource = picked ?? derived.source;
          const rows = tlsGroup ? tlsRows(g.settings, source) : g.settings;
          const hidden = tlsGroup ? g.settings.filter((s) => !rows.includes(s)) : [];
          const typedDirectory = edits['tls.acme.directory'];
          const row = (s: ConfigSetting) => (
            <SettingRow key={s.key} s={s} effective={view.effective} local={report.local ?? {}} overrides={view.overrides}
              edit={edits[s.key]} error={pending.errors[s.key] ?? errorsByKey.get(s.key)} changed={s.key in pending.changes}
              restartPending={restartKeys.includes(s.key)} onEdit={(e) => setEdit(s.key, e)}
              note={s.key === 'tls.acme.directory' ? <TestCaNote value={typedDirectory && 'value' in typedDirectory ? String(typedDirectory.value) : String(view.effective[s.key] ?? '')} /> : undefined} />
          );
          return (
            <Surface key={g.name} className="overflow-hidden">
              <button type="button" aria-expanded={open} onClick={toggle}
                className="w-full flex items-center gap-3 px-5 py-4 bg-transparent border-0 text-left cursor-pointer text-n10 hover:bg-n3/50 focus-visible:ring-[1.5px] focus-visible:ring-accent">
                <span className="text-body font-semibold flex-1">{g.name}</span>
                <span className="text-micro text-n7">{tlsGroup && source === 'off' ? 'off' : rows.length === 1 ? '1 setting' : `${rows.length} settings`}</span>
                <ChevronIcon size={16} className={cx('text-n7 transition-transform duration-[var(--t-micro)]', open && 'rotate-180')} />
              </button>
              {open && tlsGroup && (
                <TlsSourceBar source={source} incomplete={picked === null && derived.incomplete} schemaKeys={schema.map((s) => s.key)} acme={v.tls?.acme}
                  hidden={hidden} pendingChanges={pending.changes} pendingErrors={pending.errors} errorsByKey={errorsByKey}
                  onPick={(t) => { setPicked(t); setEdits((cur) => switchTlsSource(cur, t, schema)); }} />
              )}
              {open && <div>{rows.map(row)}</div>}
            </Surface>
          );
        })}
      </div>

      <Surface className="p-5 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <Kicker>Advanced</Kicker>
          <Button variant="ghost" aria-expanded={advanced} onClick={() => setAdvanced((x) => !x)} className="h-8">{advanced ? 'Hide overrides' : 'Show overrides as JSON'}</Button>
        </div>
        {advanced && (
          <div className="flex flex-col gap-2">
            <p className="m-0 text-small text-n8">What the Cloud has stored for this Vault. Read-only; change settings with the form above. Secrets show as set.</p>
            <pre className="m-0 rounded-chip bg-n3 p-3 font-mono text-micro text-n10 overflow-x-auto max-h-80" tabIndex={0} aria-label="Overrides as JSON">{overridesJson}</pre>
            <div><Button variant="secondary" onClick={() => { void navigator.clipboard?.writeText(overridesJson); toast('Copied'); }}><CopyIcon size={14} />Copy</Button></div>
          </div>
        )}
      </Surface>

      <History data={data} schema={schema} />

      {(pending.count > 0 || submitted !== undefined) && (
        <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none">
          <Surface level={2} role="region" aria-label="Unsaved changes" className="pointer-events-auto flex items-center gap-3 flex-wrap px-4 py-3 max-w-full">
            {submitted !== undefined ? (
              <span className="inline-flex items-center gap-2 text-small"><Spinner className="size-4" />Waiting for the Vault…</span>
            ) : (
              <>
                <span className="text-small font-medium tabular-nums">{changeCount(pending.count)}</span>
                {invalid > 0 && <span className="text-small text-fail inline-flex items-center gap-1.5"><AlertIcon size={14} />{invalid === 1 ? '1 value to fix' : `${invalid} values to fix`}</span>}
                {saveError && <span className="text-small text-fail">{saveError}</span>}
                <Button variant="primary" disabled={!canSave} onClick={() => void save()}>Save</Button>
                <Button variant="ghost" disabled={saving} onClick={() => { resetDraft(); setSaveError(undefined); }}>Discard</Button>
              </>
            )}
          </Surface>
        </div>
      )}
    </>
  );
}

function OfflineNotice({ online }: { online: boolean }) {
  if (online) return null;
  return (
    <Surface className="p-4">
      <Notice tone="warn" title="Vault is offline; changes are delivered when it reconnects" />
    </Surface>
  );
}

// ---------------------------------------------------------------- restart

function RestartButton({ disabled, onClick }: { disabled?: boolean; onClick: () => void }) {
  return <Button variant="secondary" disabled={disabled} onClick={onClick}><RestartIcon size={15} />Restart Vault</Button>;
}

function RestartCard({ canRestart, online, restart, confirming, setConfirming, error, setError }: {
  canRestart: boolean; online: boolean; restart: RestartCtl; confirming: boolean; setConfirming: (b: boolean) => void;
  error: string | undefined; setError: (e: string | undefined) => void;
}) {
  const s = restart.state;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { if (s.kind !== 'waiting') return; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [s.kind]);
  const busy = s.kind === 'waiting';
  const blocked = !online ? 'offline' : !canRestart ? 'unsupervised' : null;

  return (
    <Surface className="p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-0.5 min-w-[220px] flex-1">
          <Kicker>Restart</Kicker>
          <span className="text-small text-n8">Needed for settings marked “requires restart”.</span>
        </div>
        <RestartButton disabled={blocked !== null || busy || confirming} onClick={() => { setError(undefined); setConfirming(true); }} />
      </div>
      {blocked === 'offline' && <p className="m-0 text-small text-n8">The Vault is offline, so it cannot be restarted from here.</p>}
      {blocked === 'unsupervised' && (
        <div className="flex flex-col gap-2 text-small text-n8">
          <p className="m-0">Not run by a supervisor. Restart it on the server:</p>
          <Command text={RESTART_COMMAND} />
        </div>
      )}
      {confirming && (
        <Confirm question="Restart this Vault?" action="Restart" onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            setConfirming(false);
            try { await restart.begin(); } catch (e) { setError(message(e)); }
          }}>
          The Vault restarts and ingest pauses for a short moment. Connectors retry on their own.
        </Confirm>
      )}
      {error && <p className="m-0 text-small text-fail" role="alert">{error}</p>}
      {s.kind === 'waiting' && (
        <div className="flex items-center gap-3 text-body" role="status"><Spinner className="size-4" />Restarting… <span className="text-small text-n7 tabular-nums">{restartElapsed(s, now)} s</span></div>
      )}
      {s.kind === 'back' && (
        <div className="flex items-center gap-3 flex-wrap" role="status">
          <i className="size-2 rounded-full bg-ok shrink-0" aria-hidden="true" />
          <span className="text-body">Back online{s.version ? ` · running ${s.version}` : ''}{s.at ? ` · started ${dateTime(s.at)}` : ''}</span>
          <Button variant="ghost" className="h-7 px-2 text-micro ml-auto" onClick={restart.dismiss}>Dismiss</Button>
        </div>
      )}
      {s.kind === 'timeout' && (
        <div className="flex flex-col gap-3" role="alert">
          <Notice tone="fail" role="alert" title="The Vault has not come back. Go to the server">
            <div className="flex flex-col gap-2 items-start"><Command text={STATUS_COMMAND} /><Command text={JOURNAL_COMMAND} /></div>
            <p>If the new settings were the problem, the Vault goes back to the last working configuration by itself at its second start.</p>
          </Notice>
          <div><Button variant="ghost" onClick={restart.dismiss}>Dismiss</Button></div>
        </div>
      )}
    </Surface>
  );
}

// ---------------------------------------------------------------- one setting

function SettingRow({ s, effective, local, overrides, edit, error, changed, restartPending, onEdit, note }: {
  note?: ReactNode; s: ConfigSetting; effective: Values; local: Values; overrides: Values; edit: Edit | undefined; error: string | undefined; changed: boolean;
  restartPending: boolean; onEdit: (e: Edit | undefined) => void;
}) {
  const source = sourceOf(s.key, local, overrides, s.default);
  const reverting = !!edit && 'revert' in edit;
  const overridden = s.key in overrides;
  const isSecret = s.type === 'secret';
  const edited = changed || !!error;
  const id = `cfg-${s.key}`;
  const shown = reverting ? (local[s.key] ?? s.default) : (effective[s.key] ?? s.default);

  return (
    <div className="grid gap-x-8 gap-y-2 px-5 py-4 border-t border-n4 md:grid-cols-[minmax(0,1fr)_minmax(0,320px)] items-start">
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center gap-x-2.5 gap-y-1 flex-wrap">
          <label htmlFor={id} className="text-small font-medium text-n10">{s.label}</label>
          {!s.readOnly && !isSecret && (
            <span className={cx('inline-flex items-center h-5 rounded-full px-2 text-micro', source === 'cloud' && !reverting ? 'bg-accent-soft text-accent' : 'bg-n3 text-n8')}>
              {reverting ? 'vault.json' : SOURCE_LABEL[source]}
            </span>
          )}
          {s.requiresRestart && !s.readOnly && (
            <span className={cx('inline-flex items-center gap-1 text-micro', restartPending ? 'text-warn' : 'text-n8')}>
              <RestartIcon size={12} />{restartPending ? 'restart pending' : 'requires restart'}
            </span>
          )}
          {edited && <span className="text-micro text-accent">edited</span>}
        </div>
        {s.help && <p className="m-0 text-small text-n8 max-w-[62ch]">{s.help}</p>}
        <p className="m-0 font-mono text-micro text-n7 [overflow-wrap:anywhere]">{s.key}</p>
        {overridden && !isSecret && !s.readOnly && (
          <div>
            {reverting ? (
              <button type="button" onClick={() => onEdit(undefined)} className="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-micro text-accent cursor-pointer hover:underline">
                Undo revert
              </button>
            ) : (
              <button type="button" onClick={() => onEdit({ revert: true })} className="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-micro text-n8 cursor-pointer hover:text-n10">
                <RevertIcon size={12} />Revert to vault.json value
              </button>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5 min-w-0">
        <Control s={s} id={id} value={shown} edit={edit} disabled={reverting} invalid={!!error} overridden={overridden} onEdit={onEdit} />
        {error && <p className="m-0 text-micro text-fail" role="alert">{error}</p>}
        {!error && s.type === 'int' && (s.min !== undefined || s.max !== undefined) && (
          <p className="m-0 text-micro text-n7">{s.min !== undefined && s.max !== undefined ? `${s.min} to ${s.max}` : s.min !== undefined ? `at least ${s.min}` : `at most ${s.max}`}</p>
        )}
        {note}
        {reverting && <p className="m-0 text-micro text-n7">Uses the vault.json value after saving.</p>}
      </div>
    </div>
  );
}

function Control({ s, id, value, edit, disabled, invalid, overridden, onEdit }: {
  s: ConfigSetting; id: string; value: unknown; edit: Edit | undefined; disabled: boolean; invalid: boolean; overridden: boolean; onEdit: (e: Edit | undefined) => void;
}) {
  const typed = edit && 'value' in edit ? edit.value : undefined;
  const set = (v: string | boolean) => onEdit({ value: v });
  const text = value === undefined || value === null ? '' : String(value);
  const inputProps = { id, 'aria-invalid': invalid || undefined, className: cx(inputClass, invalid && 'ring-[1.5px] ring-fail', disabled && 'opacity-60') };

  if (s.readOnly) {
    return isSecretLike(s) ? <span className="text-small text-n8">{text || 'unset'}</span> : <span id={id} className="font-mono text-small text-n10 [overflow-wrap:anywhere]">{text || '—'}</span>;
  }
  switch (s.type) {
    case 'bool': {
      const on = typed !== undefined ? typed === true || typed === 'true' : value === true || value === 'true';
      return (
        <div className="flex items-center gap-3 h-9">
          <Switch label={s.label} checked={on} disabled={disabled} onChange={(b) => set(b)} />
          <span className="text-small text-n8" aria-hidden="true">{on ? 'On' : 'Off'}</span>
        </div>
      );
    }
    case 'enum':
      return (
        <select {...inputProps} value={typed !== undefined ? String(typed) : text} disabled={disabled} onChange={(e) => set(e.target.value)}>
          {!(s.options ?? []).includes(text) && text !== '' && <option value={text}>{text}</option>}
          {(s.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case 'int':
      return (
        <input {...inputProps} type="number" inputMode="numeric" step={1} min={s.min} max={s.max} disabled={disabled}
          value={typed !== undefined ? String(typed) : text} onChange={(e) => set(e.target.value)} />
      );
    case 'secret': {
      const isSet = text === 'set';
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <i className={cx('size-2 rounded-full shrink-0', isSet ? 'bg-ok' : 'bg-n6')} aria-hidden="true" />
            <span className="text-small text-n9">{isSet ? 'set' : 'unset'}</span>
            {overridden && isSet && !(typed !== undefined && typed !== '') && (
              <Button variant="ghost" className="h-7 px-2 text-micro ml-auto" onClick={() => onEdit({ revert: true })}>Clear</Button>
            )}
          </div>
          <input {...inputProps} type="password" autoComplete="new-password" placeholder={isSet ? 'Enter a new value to replace it' : 'Enter a value'}
            value={typed !== undefined ? String(typed) : ''} onChange={(e) => set(e.target.value)} />
          {edit && 'revert' in edit && <p className="m-0 text-micro text-n7">Cleared when you save. <button type="button" onClick={() => onEdit(undefined)} className="bg-transparent border-0 p-0 text-accent cursor-pointer text-micro hover:underline">Undo</button></p>}
        </div>
      );
    }
    default:
      return <input {...inputProps} type="text" autoComplete="off" spellCheck={false} disabled={disabled} value={typed !== undefined ? String(typed) : text} onChange={(e) => set(e.target.value)} />;
  }
}

const isSecretLike = (s: ConfigSetting) => s.type === 'secret' || !!s.sensitive;

// ---------------------------------------------------------------- history

function History({ data, schema }: { data: VaultConfigData; schema: ConfigSetting[] }) {
  return (
    <Surface className="p-5 flex flex-col gap-3">
      <Kicker>Changes</Kicker>
      {data.history.length === 0 ? (
        <p className="m-0 text-small text-n8">Nothing has been changed from the Cloud yet.</p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col">
          {data.history.map((h, i) => (
            <li key={h.version} className={cx('py-2.5 grid gap-x-4 gap-y-1 sm:grid-cols-[64px_minmax(0,1fr)_auto] items-baseline', i > 0 && 'border-t border-n4')}>
              <span className="font-mono text-micro text-n8">v{h.version}</span>
              <span className="text-small text-n10 min-w-0 [overflow-wrap:anywhere]">
                {h.changedKeys.length === 0 ? <span className="text-n7">no keys</span> : h.changedKeys.map((k, j) => (
                  <span key={k}>{j > 0 && ', '}<span title={k}>{labelOf(schema, k)}</span></span>
                ))}
              </span>
              <span className="text-micro text-n7 sm:text-right whitespace-nowrap">
                {authorName(h.user)} · <time dateTime={new Date(h.at).toISOString()} title={dateTime(h.at)}>{ago(h.at)}</time>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}


// ---------------------------------------------------------------- HTTPS ingest: the certificate source

/** The settings of the group that the chosen source shows, in its order (unknown keys of a newer Vault follow, when HTTPS is on). */
function tlsRows(settings: ConfigSetting[], source: TlsSource): ConfigSetting[] {
  const byKey = new Map(settings.map((s) => [s.key, s]));
  const known = new Set<string>([...TLS_IMPLIED_KEYS, ...Object.values(TLS_SOURCE_KEYS).flat()]);
  const rows = TLS_SOURCE_KEYS[source].map((k) => byKey.get(k)).filter((s): s is ConfigSetting => !!s);
  if (source !== 'off') rows.push(...settings.filter((s) => !known.has(s.key)));
  return rows;
}

const draftValue = (v: SettingValue | null | undefined) => v === '' ? 'cleared' : v === true ? 'on' : v === false ? 'off' : v === null ? 'removed' : String(v);

function TlsSourceBar({ source, incomplete, schemaKeys, acme, hidden, pendingChanges: changes, pendingErrors, errorsByKey, onPick }: {
  source: TlsSource; incomplete: boolean; schemaKeys: string[]; acme: AcmeInfo | null | undefined; hidden: ConfigSetting[];
  pendingChanges: Record<string, SettingValue | null>; pendingErrors: Record<string, string>; errorsByKey: Map<string, string>; onPick: (s: TlsSource) => void;
}) {
  const offered = TLS_SOURCES.filter((t) => t !== 'acme' || schemaKeys.includes('tls.acme.enabled'));
  const switched = hidden.filter((s) => s.key in changes);
  const failing = hidden.map((s) => ({ s, message: pendingErrors[s.key] ?? errorsByKey.get(s.key) })).filter((x) => x.message);
  const st = acme?.enabled ? acmeStateView(acme.state) : null;
  return (
    <div className="px-5 pb-4 flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <span className="text-small font-medium text-n10" id="tls-source-label">Certificate source</span>
        <span className="flex flex-wrap self-start max-w-full bg-n3 rounded-chip p-0.5 gap-0.5" role="group" aria-labelledby="tls-source-label">
          {offered.map((t) => <Pill key={t} active={t === source} onClick={() => onPick(t)}>{t === 'off' ? 'HTTPS ingest: Off' : TLS_SOURCE_LABEL[t]}</Pill>)}
        </span>
        <p className="m-0 text-small text-n8 max-w-[62ch]" data-tls-source={source}>{TLS_SOURCE_HELP[source]}</p>
        {incomplete && (
          <p className="m-0 text-small text-warn inline-flex items-start gap-1.5" role="status">
            <AlertIcon size={14} className="mt-0.5 shrink-0" />
            <span>HTTPS ingest is switched on (<span className="font-mono text-micro">tls.enabled</span>) but no certificate source is set up, so it cannot work. Choose one.</span>
          </p>
        )}
        {source === 'acme' && (
          <p className="m-0 text-small text-n8 max-w-[62ch]">Turning this on means you agree to the CA's terms of service (for Let's Encrypt: letsencrypt.org/repository). It cannot be combined with certificate files or a keystore.</p>
        )}
      </div>
      {source === 'acme' && acme?.enabled && st && (
        <dl className="m-0 flex flex-col rounded-chip bg-n3 px-4 py-3 gap-1.5" aria-label="Certificate status">
          <StatusFact label="Status">
            <span className={cx('inline-flex items-center gap-1.5', st.tone === 'fail' ? 'text-fail' : st.tone === 'ok' ? 'text-ok' : 'text-n10')} data-acme-state={acme.state}>
              {st.tone === 'busy' ? <Spinner className="size-3.5" /> : st.tone === 'ok' ? <CheckIcon size={13} /> : st.tone === 'fail' ? <AlertIcon size={13} /> : <i className="size-2 rounded-full bg-n6" aria-hidden="true" />}
              {st.label}
            </span>
          </StatusFact>
          <StatusFact label="Issued">{acme.lastIssuedAt ? <>{dateTime(acme.lastIssuedAt)}<span className="text-n8"> · {relativeWords(acme.lastIssuedAt)}</span></> : 'never'}</StatusFact>
          <StatusFact label="Next renewal">{acme.nextRenewalAt ? <>{dateTime(acme.nextRenewalAt)}<span className="text-n8"> · {relativeWords(acme.nextRenewalAt)}</span></> : '—'}</StatusFact>
          {acme.lastError && (
            <StatusFact label="Last error"><span className="text-fail">{acme.lastError.message}</span><span className="text-n7"> · {relativeWords(acme.lastError.at)}</span></StatusFact>
          )}
        </dl>
      )}
      {switched.length > 0 && (
        <p className="m-0 text-small text-n8" data-tls-implied>
          This choice also sets: {switched.map((s, i) => (
            <span key={s.key}>{i > 0 && ' · '}<span className="text-n10">{s.label}</span> <span className="font-mono text-micro text-n7">{s.key}</span> {draftValue(changes[s.key])}</span>
          ))}
        </p>
      )}
      {failing.length > 0 && (
        <ul className="m-0 pl-4 flex flex-col gap-1 text-small text-fail" role="alert">
          {failing.map(({ s, message }) => <li key={s.key}><span className="font-mono text-micro">{s.key}</span>: {message}</li>)}
        </ul>
      )}
    </div>
  );
}

function StatusFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[96px_1fr] gap-3 items-baseline text-small">
      <dt className="text-n8">{label}</dt>
      <dd className="m-0 text-n10 min-w-0 [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function TestCaNote({ value }: { value: string }) {
  if (!directoryInfo(value).testCa) return null;
  return (
    <p className="m-0 text-micro text-warn inline-flex items-start gap-1.5">
      <AlertIcon size={12} className="mt-0.5 shrink-0" />Test CA: the certificates it issues are not trusted by browsers or senders. Use it to try the setup, then switch to production.
    </p>
  );
}
